import { Action } from '@reduxjs/toolkit';
import { put, select } from 'redux-saga/effects';
import { addActionNotification } from 'features/notifications/action-notifications/addActionNotification';
import i18n from 'i18n';
import { logger } from 'utils/logger';
import { User } from 'features/users/types';
import { selectUserById } from 'features/users/usersSlice';
import { audioUnmuteAsked, remoteAudioUnmuteAccepted } from 'features/streaming/actions';
import { selectMediaDevicePermissions } from 'features/user-media/userMediaSlice';
import { PermissionStatus } from 'features/user-media/types';

export function* onAudioUnmuteAskedSaga({
  payload: { data, initiator },
}: ReturnType<typeof audioUnmuteAsked>) {
  // Backend broadcasts this event to both the target user (no `data`) and to
  // moderators with ASK_REMOTE_UNMUTE permission (with `data.id` set). Only the
  // target should see the unmute prompt — moderators consume the same action
  // through `usersSlice` to drive the pending-state icon.
  if (data) {
    return;
  }

  logger.remote({ tier: 1 }).info(`User was asked to unmute by user id=${initiator.id}`);

  const user: User = yield select(selectUserById, initiator.id);
  const micPermissions: PermissionStatus = yield select(selectMediaDevicePermissions, 'audioinput');

  if (micPermissions === 'granted') {
    yield put(
      addActionNotification({
        content: i18n.t('notifications:unmute_remote_audio_request.content', {
          username: user.name,
        }),
        cancelText: i18n.t('notifications:unmute_remote_audio_request.btn_cancel_label'),
        okText: i18n.t('notifications:unmute_remote_audio_request.btn_ok_label'),
        okAction: remoteAudioUnmuteAccepted(),
        initiatorId: initiator.id,
        interaction: 'unmuteAudio',
        scope: ['broadcast'],
      }) as unknown as Action
    );
  } else {
    yield put(
      addActionNotification({
        content: i18n.t('notifications:unmute_remote_audio_request_no_permissions.content', {
          username: user.name,
        }),
        okText: i18n.t('notifications:unmute_remote_audio_request_no_permissions.btn_ok_label'),
        initiatorId: initiator.id,
      }) as unknown as Action
    );
  }
}
